/**
 * File: OCRTranslationPage.jsx
 * Path: /src/pages/OCRTranslationPage.jsx
 * Description: Dedicated page showcasing Veritect.ai's OCR & Translation AI solution
 * Key Features: Interactive OCR and translation visualizations, feature breakdown, and benefits section
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Globe, Upload, Check, RefreshCw } from 'lucide-react';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ModernCtaSection from '../components/common/ModernCtaSection';
import ModernStatsSection from '../components/common/ModernStatsSection';
import OCRMetricsDashboard from '../components/solutions/OCRMetricsDashboard';
import TranslationDashboard from '../components/solutions/TranslationDashboard';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

const OCRTranslationPage = () => {
  // Initialize observer for scroll animations
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  const [activeTab, setActiveTab] = useState('ocr');
  const [isLoading, setIsLoading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Simulated file processing
  const handleFileUpload = (e) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate processing time
    setTimeout(() => {
      setIsLoading(false);
      setFileUploaded(true);
    }, 2000);
  };

  // Features data for OCR
  const ocrFeatures = [
    {
      title: "Advanced Document OCR",
      description: "Extract text from any document format with 99.8% accuracy, including handwritten text and complex layouts.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Layout Preservation",
      description: "Maintain original document structure including tables, columns, and formatting for accurate representation.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      )
    },
    {
      title: "Metadata Extraction",
      description: "Automatically identify and extract key document metadata like dates, parties, case numbers, and jurisdictions.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  // Features data for Translation
  const translationFeatures = [
    {
      title: "Legal-Specialized Translation",
      description: "AI translation engine trained on millions of legal documents across 50+ languages with jurisdiction-specific terminology.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      )
    },
    {
      title: "Context-Aware Translation",
      description: "Understands legal contexts and maintains accurate meaning across languages, not just word-for-word translation.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Terminology Management",
      description: "Maintain consistent terminology across all translated documents with custom glossaries and term bases.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    }
  ];

  // How it works steps for OCR
  const ocrWorkflowSteps = [
    {
      number: "01",
      title: "Upload Documents",
      description: "Upload any document format - scanned PDFs, images, or existing digital documents."
    },
    {
      number: "02",
      title: "AI Processing",
      description: "Our advanced OCR engine extracts text while preserving layout and formatting."
    },
    {
      number: "03",
      title: "Review & Edit",
      description: "Review the extracted text with our side-by-side comparison tool and make any needed edits."
    },
    {
      number: "04",
      title: "Export & Use",
      description: "Export in your preferred format (Word, PDF, plain text) or send directly to translation."
    }
  ];

  // How it works steps for Translation
  const translationWorkflowSteps = [
    {
      number: "01",
      title: "Select Content",
      description: "Upload documents or paste text for translation, or use already processed OCR content."
    },
    {
      number: "02",
      title: "Choose Languages",
      description: "Select source and target languages from our supported list of 50+ languages."
    },
    {
      number: "03",
      title: "AI Translation",
      description: "Our legal-specialized AI translates the content maintaining legal terminology accuracy."
    },
    {
      number: "04",
      title: "Review & Export",
      description: "Review the translation with our parallel view tool, make edits, and export in any format."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-indigo-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-purple-100 rounded-tr-full opacity-70"></div>

        {/* Floating decorative elements */}
        <motion.div
          className="absolute right-1/4 top-1/5 hidden md:block"
          animate={{
            y: [0, -15, 0],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <div className="w-16 h-16 border-2 border-indigo-500/30 rounded-lg rotate-12"></div>
        </motion.div>

        <motion.div
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          animate={{
            y: [0, 15, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 1
          }}
        >
          <div className="w-12 h-12 border-2 border-purple-500/30 rounded-full"></div>
        </motion.div>

        <div className="container-padding mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  AI-Powered <span className="gradient-text">OCR & Translation</span> Solutions
                </h1>
                <p className="text-xl text-dark-600 mb-8">
                  Transform and analyze legal documents with our cutting-edge OCR technology and AI-powered translation services. Quickly extract text from images and translate content across 50+ languages with legal terminology precision.
                </p>

                <div className="flex flex-wrap gap-4 mb-10">
                  <Button
                    variant="primary"
                    size="lg"
                    to="/contact"
                    className="shadow-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">Request a Demo</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    to="#how-it-works"
                  >
                    See How It Works
                  </Button>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="flex -space-x-2">
                    {['#6366F1', '#8B5CF6', '#A855F7', '#D946EF'].map((color, i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: color, zIndex: 4-i }}>
                        {['A', 'B', 'C', 'D'][i]}
                      </div>
                    ))}
                  </div>
                  <div className="text-dark-500">
                    <strong className="text-dark-700">50+</strong> supported languages
                  </div>
                </div>
              </motion.div>

              {/* Right Side - Demo UI */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <div className="bg-gradient-to-br from-indigo-950 via-blue-900 to-purple-900 p-6">
                    {/* Floating decorative elements */}
                    <div className="absolute inset-0 z-0">
                      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-blue-500 opacity-10 blur-3xl animate-pulse"></div>
                      <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-purple-500 opacity-10 blur-3xl animate-pulse delay-1000"></div>
                    </div>

                    <div className="relative z-10">
                      {/* UI Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center mr-4">
                            {activeTab === 'ocr' ? (
                              <FileText className="h-6 w-6 text-white" />
                            ) : (
                              <Globe className="h-6 w-6 text-white" />
                            )}
                          </div>
                          <h3 className="font-bold text-xl text-white">
                            Veritect {activeTab === 'ocr' ? 'OCR' : 'Translation'} AI
                          </h3>
                        </div>
                        <div className="flex space-x-2">
                          <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="backdrop-blur-lg bg-white/10 rounded-xl p-1 mb-6 inline-flex">
                        <button
                          onClick={() => setActiveTab('ocr')}
                          className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                            activeTab === 'ocr'
                              ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                              : 'text-white/80 hover:text-white'
                          }`}
                        >
                          OCR Processing
                        </button>
                        <button
                          onClick={() => setActiveTab('translation')}
                          className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                            activeTab === 'translation'
                              ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                              : 'text-white/80 hover:text-white'
                          }`}
                        >
                          Translation
                        </button>
                      </div>

                      {/* Upload Area */}
                      {!fileUploaded ? (
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-xl mb-6">
                          <h3 className="text-xl font-semibold mb-4 text-white">
                            {activeTab === 'ocr' ? 'Upload Documents for OCR' : 'Upload Content for Translation'}
                          </h3>

                          <form onSubmit={handleFileUpload}>
                            <div className="w-full h-48 border-2 border-dashed border-indigo-300/50 rounded-lg flex items-center justify-center mb-6 hover:border-indigo-400/70 transition-colors cursor-pointer bg-indigo-900/20">
                              {isLoading ? (
                                <div className="flex flex-col items-center justify-center">
                                  <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3"></div>
                                  <p className="text-indigo-200">Processing...</p>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center">
                                  <Upload className="h-10 w-10 text-indigo-300 mb-3" />
                                  <p className="text-indigo-200 mb-2">Drag and drop your files here</p>
                                  <p className="text-indigo-300/70 text-sm">or</p>
                                  <button type="button" className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                                    Browse Files
                                  </button>
                                </div>
                              )}
                            </div>

                            <div className="flex justify-between items-center">
                              <div className="text-sm text-indigo-200">
                                {activeTab === 'ocr' ?
                                  'Supported formats: PDF, JPG, PNG, TIFF' :
                                  'Supported formats: PDF, DOCX, TXT, HTML'
                                }
                              </div>
                              <Button
                                variant="primary"
                                type="submit"
                                disabled={isLoading}
                                className="bg-indigo-600 hover:bg-indigo-700"
                              >
                                {activeTab === 'ocr' ? 'Process Document' : 'Translate Content'}
                              </Button>
                            </div>
                          </form>
                        </div>
                      ) : (
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 shadow-xl mb-6">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-white">
                              {activeTab === 'ocr' ? 'OCR Results' : 'Translation Results'}
                            </h3>
                            <div className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs flex items-center">
                              <Check className="h-3 w-3 mr-1" />
                              Complete
                            </div>
                          </div>

                          <div className="bg-white/5 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto text-sm text-white/90">
                            {activeTab === 'ocr' ? (
                              <div>
                                <p className="mb-2">CASE NO. CV-2023-45678</p>
                                <p className="mb-2">SUPERIOR COURT OF THE STATE OF CALIFORNIA</p>
                                <p className="mb-2">COUNTY OF LOS ANGELES, CENTRAL DISTRICT</p>
                                <p className="mb-2">JANE DOE, an individual,</p>
                                <p className="mb-2">Plaintiff,</p>
                                <p className="mb-2">v.</p>
                                <p className="mb-2">ACME CORPORATION, a Delaware corporation; and DOES 1-10, inclusive,</p>
                                <p className="mb-2">Defendants.</p>
                              </div>
                            ) : (
                              <div>
                                <p className="mb-2">NÚMERO DE CASO CV-2023-45678</p>
                                <p className="mb-2">TRIBUNAL SUPERIOR DEL ESTADO DE CALIFORNIA</p>
                                <p className="mb-2">CONDADO DE LOS ÁNGELES, DISTRITO CENTRAL</p>
                                <p className="mb-2">JANE DOE, una persona física,</p>
                                <p className="mb-2">Demandante,</p>
                                <p className="mb-2">contra</p>
                                <p className="mb-2">ACME CORPORATION, una sociedad de Delaware; y DOES 1-10, inclusive,</p>
                                <p className="mb-2">Demandados.</p>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex space-x-4">
                              <div className="flex-1 bg-white/5 rounded-lg p-2">
                                <div className="text-xs text-white/60 mb-1">
                                  {activeTab === 'ocr' ? 'Accuracy Score' : 'Translation Confidence'}
                                </div>
                                <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                                  <div className="w-11/12 h-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
                                </div>
                                <div className="text-right text-xs font-medium text-white mt-1">96%</div>
                              </div>

                              <div className="flex-1 bg-white/5 rounded-lg p-2">
                                <div className="text-xs text-white/60 mb-1">Processing Time</div>
                                <div className="text-sm font-medium text-white">00:02.34</div>
                              </div>
                            </div>

                            <Button
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/10"
                              onClick={() => setFileUploaded(false)}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Process Another
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <ModernStatsSection
        title="Transform Your Document Processing"
        description="See the real impact our AI-powered OCR and translation platform is making for law firms."
        stats={[
          { number: '99.8%', label: 'OCR Accuracy Rate' },
          { number: '50+', label: 'Supported Languages' },
          { number: '95%', label: 'Time Saved on Translations' },
          { number: '4.2M+', label: 'Documents Processed' }
        ]}
      />

      {/* Features Section */}
      <section className="py-20 bg-white" ref={sectionRef}>
        <div className="container-padding mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Key Features</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              {activeTab === 'ocr' ? 'Powerful OCR Capabilities' : 'Advanced Translation Features'}
            </h2>
            <p className="text-dark-500 text-lg">
              {activeTab === 'ocr' ?
                'Our OCR technology combines cutting-edge AI with deep legal expertise to deliver document processing that\'s faster, more accurate, and more insightful.' :
                'Our legal translation platform combines cutting-edge AI with deep legal expertise to deliver translations that are accurate, context-aware, and jurisdiction-specific.'
              }
            </p>

            {/* Tabs */}
            <div className="flex justify-center mt-8 mb-8">
              <div className="inline-flex bg-primary-50 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('ocr')}
                  className={`px-6 py-2 rounded-md font-medium transition-all duration-300 ${
                    activeTab === 'ocr'
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                      : 'text-primary-700 hover:text-primary-900'
                  }`}
                >
                  OCR Features
                </button>
                <button
                  onClick={() => setActiveTab('translation')}
                  className={`px-6 py-2 rounded-md font-medium transition-all duration-300 ${
                    activeTab === 'translation'
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                      : 'text-primary-700 hover:text-primary-900'
                  }`}
                >
                  Translation Features
                </button>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(activeTab === 'ocr' ? ocrFeatures : translationFeatures).map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full flex flex-col items-start">
                  <div className="rounded-lg p-3 bg-indigo-50 text-indigo-600 mb-5">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-dark-500">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Display the appropriate dashboard based on the active tab */}
      <section className="py-20 bg-primary-50">
        <div className="container-padding mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Metrics Dashboard</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              {activeTab === 'ocr' ? 'OCR Performance Analytics' : 'Translation Quality Metrics'}
            </h2>
            <p className="text-dark-500 text-lg">
              {activeTab === 'ocr' ?
                'Monitor OCR accuracy, processing speed, and document volumes with our comprehensive analytics dashboard.' :
                'Track translation quality, language pair performance, and terminology consistency with our advanced metrics.'
              }
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            {activeTab === 'ocr' ? <OCRMetricsDashboard /> : <TranslationDashboard />}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-indigo-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-purple-600/5 rounded-full blur-3xl"></div>

        <div className="container-padding mx-auto relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              {/* CORRECTED THIS LINE */}
              {activeTab === 'ocr' ? 'OCR Process, Simplified' : 'Translation Process, Reimagined'}
            </h2>
            <p className="text-dark-500 text-lg">
              Our intuitive workflow makes document processing and translation faster and more effective than ever before.
            </p>

            {/* Tabs */}
            <div className="flex justify-center mt-8 mb-8">
              <div className="inline-flex bg-primary-50 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('ocr')}
                  className={`px-6 py-2 rounded-md font-medium transition-all duration-300 ${
                    activeTab === 'ocr'
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                      : 'text-primary-700 hover:text-primary-900'
                  }`}
                >
                  OCR Process
                </button>
                <button
                  onClick={() => setActiveTab('translation')}
                  className={`px-6 py-2 rounded-md font-medium transition-all duration-300 ${
                    activeTab === 'translation'
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                      : 'text-primary-700 hover:text-primary-900'
                  }`}
                >
                  Translation Process
                </button>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {(activeTab === 'ocr' ? ocrWorkflowSteps : translationWorkflowSteps).map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              >
                {/* Connector line between steps (hidden on mobile) */}
                {index < (activeTab === 'ocr' ? ocrWorkflowSteps.length : translationWorkflowSteps.length) - 1 && (
                  <div className="absolute hidden lg:block top-6 left-full w-full h-0.5 bg-indigo-200 z-0">
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45">
                      <svg className="w-3 h-3 text-indigo-400" fill="currentColor" viewBox="0 0 256 512">
                        <path d="M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z"></path>
                      </svg>
                    </div>
                  </div>
                )}

                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-indigo-600 text-white text-xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-dark-500">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-primary-50">
        <div className="container-padding mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-primary-600 font-semibold">Benefits</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Why Choose Veritect {activeTab === 'ocr' ? 'OCR' : 'Translation'} AI
              </h2>
              <p className="text-dark-600 mb-8">
                {activeTab === 'ocr' ?
                  'Our AI-powered OCR technology doesn\'t just extract text—it transforms how legal professionals handle document processing, enabling deeper insights and better workflow efficiency.' :
                  'Our AI-powered legal translation platform doesn\'t just translate words—it preserves legal meaning across languages, enabling global operations with local precision.'
                }
              </p>

              <div className="space-y-6">
                {activeTab === 'ocr' ? (
                  // OCR Benefits
                  <>
                    <motion.div
                      className="flex items-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="mr-4 text-indigo-600 mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">Dramatic Time Savings</h4>
                        <p className="text-gray-600">Transform hours of manual data entry and document processing into minutes, freeing up valuable resources for higher-value legal work.</p>
                      </div>
                    </motion.div>

                    <motion.div
                      className="flex items-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 0.4 }}
                    >
                      <div className="mr-4 text-indigo-600 mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">Superior Accuracy</h4>
                        <p className="text-gray-600">Our specialized legal OCR achieves 99.8% accuracy, even with complex legal formatting, tables, and challenging document quality.</p>
                      </div>
                    </motion.div>

                    <motion.div
                      className="flex items-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="mr-4 text-indigo-600 mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">Seamless Integration</h4>
                        <p className="text-gray-600">Integrates with your document management system, case management software, and our translation services for end-to-end document processing.</p>
                      </div>
                    </motion.div>
                  </>
                ) : (
                  // Translation Benefits
                  <>
                    <motion.div
                      className="flex items-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="mr-4 text-indigo-600 mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">Legal Terminology Precision</h4>
                        <p className="text-gray-600">Our AI is trained specifically on legal terminology across jurisdictions, ensuring accurate translations of specialized terms and concepts.</p>
                      </div>
                    </motion.div>

                    <motion.div
                      className="flex items-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 0.4 }}
                    >
                      <div className="mr-4 text-indigo-600 mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">Cost Reduction</h4>
                        <p className="text-gray-600">Reduce translation costs by up to 80% compared to traditional legal translation services while maintaining quality and confidentiality.</p>
                      </div>
                    </motion.div>

                    <motion.div
                      className="flex items-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="mr-4 text-indigo-600 mt-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">Global Accessibility</h4>
                        <p className="text-gray-600">Expand your firm's reach with the ability to work with clients, documents, and cases in over 50 languages with confidence.</p>
                      </div>
                    </motion.div>
                  </>
                )}
              </div>

              <div className="mt-10">
                <Button
                  variant="primary"
                  to="/contact"
                  className="shadow-lg"
                >
                  Schedule a Demo
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Display example */}
              <div className="relative rounded-xl overflow-hidden shadow-xl">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-1">
                  <div className="bg-white rounded-lg p-6 relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center mr-3">
                          {activeTab === 'ocr' ? (
                            <FileText className="h-5 w-5 text-white" />
                          ) : (
                            <Globe className="h-5 w-5 text-white" />
                          )}
                        </div>
                        <span className="font-semibold text-dark-600">
                          {activeTab === 'ocr' ? 'Document Processing Example' : 'Translation Example'}
                        </span>
                      </div>
                      <div className="py-1 px-3 text-xs bg-green-50 text-green-600 rounded-full">Complete</div>
                    </div>

                    {activeTab === 'ocr' ? (
                      // OCR Example
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border border-dark-100 rounded-lg p-3">
                          <div className="text-xs text-dark-400 mb-2">Original Document</div>
                          <div className="aspect-[3/4] bg-dark-100 rounded-md flex items-center justify-center">
                            <img
                              src="/api/placeholder/300/400"
                              alt="Sample document"
                              className="max-w-full max-h-full rounded-md"
                            />
                          </div>
                        </div>
                        <div className="border border-dark-100 rounded-lg p-3">
                          <div className="text-xs text-dark-400 mb-2">Extracted Text</div>
                          <div className="h-[300px] overflow-y-auto text-sm text-dark-700 p-2">
                            <p className="mb-2">EMPLOYMENT AGREEMENT</p>
                            <p className="mb-2">THIS EMPLOYMENT AGREEMENT ("Agreement") is made and entered into as of May 4, 2025 ("Effective Date"), by and between Acme Legal Services, Inc., a Delaware corporation ("Employer"), and Jane Smith, an individual residing in the State of California ("Employee").</p>
                            <p className="mb-2">WITNESSETH:</p>
                            <p className="mb-2">WHEREAS, Employer desires to employ Employee as Senior Legal Counsel, and Employee desires to be employed by Employer in such position;</p>
                            <p className="mb-2">NOW, THEREFORE, in consideration of the mutual covenants and agreements set forth herein, Employer and Employee, intending to be legally bound, hereby agree as follows:</p>
                            <p className="mb-2">1. EMPLOYMENT. Employer hereby employs Employee as Senior Legal Counsel, and Employee hereby accepts such employment, upon the terms and conditions set forth in this Agreement.</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Translation Example
                      <div className="grid grid-cols-2 gap-4">
                        <div className="border border-dark-100 rounded-lg p-3">
                          <div className="text-xs text-dark-400 mb-2">Original (English)</div>
                          <div className="h-[300px] overflow-y-auto text-sm text-dark-700 p-2">
                            <p className="mb-2">CONFIDENTIALITY AGREEMENT</p>
                            <p className="mb-2">THIS CONFIDENTIALITY AGREEMENT ("Agreement") is entered into as of May 4, 2025 ("Effective Date"), by and between ABC Corporation, a Delaware corporation ("Disclosing Party"), and XYZ Legal Services, a California professional corporation ("Receiving Party").</p>
                            <p className="mb-2">1. PURPOSE. The parties wish to explore a potential business relationship regarding legal technology services ("Purpose"). In connection with the Purpose, Disclosing Party may disclose to Receiving Party certain confidential and proprietary information.</p>
                            <p className="mb-2">2. CONFIDENTIAL INFORMATION. "Confidential Information" means any information disclosed by Disclosing Party to Receiving Party, either directly or indirectly, in writing, orally or by inspection of tangible objects, which is designated as "Confidential," "Proprietary" or some similar designation.</p>
                          </div>
                        </div>
                        <div className="border border-dark-100 rounded-lg p-3">
                          <div className="text-xs text-dark-400 mb-2">Translation (Spanish)</div>
                          <div className="h-[300px] overflow-y-auto text-sm text-dark-700 p-2">
                            <p className="mb-2">ACUERDO DE CONFIDENCIALIDAD</p>
                            <p className="mb-2">ESTE ACUERDO DE CONFIDENCIALIDAD ("Acuerdo") se celebra a partir del 4 de mayo de 2025 ("Fecha Efectiva"), por y entre ABC Corporation, una sociedad de Delaware ("Parte Divulgadora"), y XYZ Servicios Legales, una sociedad profesional de California ("Parte Receptora").</p>
                            <p className="mb-2">1. PROPÓSITO. Las partes desean explorar una potencial relación comercial en relación con servicios de tecnología legal ("Propósito"). En relación con el Propósito, la Parte Divulgadora podrá revelar a la Parte Receptora cierta información confidencial y de propiedad exclusiva.</p>
                            <p className="mb-2">2. INFORMACIÓN CONFIDENCIAL. "Información Confidencial" significa cualquier información revelada por la Parte Divulgadora a la Parte Receptora, ya sea directa o indirectamente, por escrito, oralmente o mediante inspección de objetos tangibles, que se designe como "Confidencial", "Propietaria" o alguna designación similar.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Floating elements for visual interest */}
                <div className="absolute -top-6 -right-6 w-12 h-12 bg-indigo-100 rounded-lg rotate-12 flex items-center justify-center z-20">
                  {activeTab === 'ocr' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-dark-500 text-lg">
              Everything you need to know about our {activeTab === 'ocr' ? 'OCR' : 'Translation'} AI platform
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {(activeTab === 'ocr' ? [
              {
                question: "What types of documents can your OCR process?",
                answer: "Our OCR technology can process virtually any document type, including scanned PDFs, images (JPG, PNG, TIFF), faxes, and even documents with handwritten notes. We specialize in legal documents like contracts, court filings, discovery materials, and corporate records regardless of formatting complexity."
              },
              {
                question: "How accurate is the OCR for legal documents?",
                answer: "Our legal-specialized OCR achieves 99.8% accuracy for standard legal documents and 95%+ accuracy for handwritten content. The system is trained specifically on legal formatting, terminology, and document structures, making it significantly more accurate than general-purpose OCR solutions."
              },
              {
                question: "How does the system handle poor quality documents?",
                answer: "Our OCR uses advanced image pre-processing techniques to enhance document quality before processing. This includes deskewing, denoising, contrast enhancement, and specialized algorithms for dealing with artifacts from copying or scanning. Even with low-quality originals, we typically achieve 90%+ accuracy."
              },
              {
                question: "Can the OCR extract data from tables and forms?",
                answer: "Yes, our OCR excels at maintaining document structure, including tables, columns, and form fields. It can intelligently extract tabular data while preserving relationships between cells, making it ideal for financial exhibits, timesheets, billing records, and structured legal forms."
              }
            ] : [
              {
                question: "How many languages does your translation platform support?",
                answer: "Our platform currently supports 50+ languages, including all major European, Asian, and Middle Eastern languages. We specialize in legal translations between common language pairs like English-Spanish, English-French, English-German, English-Chinese, and English-Japanese, with additional language support being added regularly."
              },
              {
                question: "How does your AI handle specialized legal terminology?",
                answer: "Our AI translation engine is trained on millions of legal documents across multiple jurisdictions and practice areas. It includes specialized terminology databases for different legal systems (common law, civil law, etc.) and practice areas (corporate, litigation, IP, etc.), ensuring accurate translation of technical terms."
              },
              {
                question: "Is the translation completely automated or human-reviewed?",
                answer: "Our platform offers both fully automated translation and human-in-the-loop options. For highly sensitive or complex documents, we recommend our hybrid approach where AI performs the initial translation, and it's then reviewed by a legal language specialist to ensure perfect accuracy."
              },
              {
                question: "How do you handle confidentiality and security of documents?",
                answer: "We maintain strict confidentiality and security standards. All data is encrypted both in transit and at rest, and we use isolated processing environments for each client. We are compliant with industry standards like ISO 27001, GDPR, and CCPA, and we can sign custom NDAs as required."
              }
            ]).map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-dark-600">{faq.answer}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <ModernCtaSection
        title={`Ready to Transform Your ${activeTab === 'ocr' ? 'Document Processing' : 'Legal Translation'} Workflow?`}
        description={`Join innovative law firms using Veritect.ai to ${activeTab === 'ocr' ? 'extract and process document content' : 'translate legal documents'} with unparalleled accuracy and efficiency.`}
        buttonText="Schedule a Demo"
        buttonLink="/contact"
      />
    </motion.div>
  );
};

export default OCRTranslationPage;